import { AuthMethod } from '@infotech-soe/merps/types/session';
import { tg } from '@infotech-soe/metaschema';
import {
  DSAgents,
  KeyTypes,
  SignAlgo,
} from '@infotech-soe/mia-ds-service/types';

import { httpGet } from '../../infrastructure/network/fetch-wrapper';
import ILocalConfigurationService from '../configuration/ILocalConfigurationService';
import LocalConfigurationService from '../configuration/LocalConfigurationService';

import {
  IRuntimeApiConfig,
  IRuntimeConfigService,
} from './IRuntimeConfigService';

export class RuntimeApiConfig implements IRuntimeApiConfig {
  public url = '';
  connectOptions?: {
    heartbeatInterval?: number;
    maxDumbHeartbeats?: number;
  };
  public excludedApps?: string[] = [];
  public excludedAuthMethods?: AuthMethod[] = [];
  public excludedEDSAgents?: DSAgents[] = [];
  public dsKeyType?: KeyTypes;
  public allowedDSMediaTypes?: string;
  public forbiddenDSMediaTypes?: string;
  public pivotLicenseKey?: string;
  public stiLicenseKey?: string;
  public signAlgo?: SignAlgo;
  public userGuideUrl?: string;
}

class RuntimeConfigServiceSingleton implements IRuntimeConfigService {
  private readonly localConfigurationService: ILocalConfigurationService;
  constructor() {
    this.localConfigurationService = new LocalConfigurationService();
  }

  public async getRuntimeApiConfig(): Promise<IRuntimeApiConfig> {
    let response: Response;

    try {
      response = await httpGet(process.env.API_CONFIG_PATH ?? '');
    } catch {
      const result = new RuntimeApiConfig();
      result.url = this.localConfigurationService.getDefaultConnectionURL();
      return result;
    }

    const json: Record<string, unknown> = await response.json();
    const config = new RuntimeApiConfig();
    const errors: string[] = [];

    if (tg.hasStringProperty(json, 'url')) {
      config.url = json.url;
    } else {
      config.url = this.localConfigurationService.getDefaultConnectionURL();
    }
    if (tg.hasProperty(json, 'connectOptions')) {
      if (tg.isObject(json.connectOptions)) {
        const connectOptions = json.connectOptions;
        const parsedConnectOptions: {
          heartbeatInterval?: number;
          maxDumbHeartbeats?: number;
        } = {};

        if (tg.hasProperty(connectOptions, 'heartbeatInterval')) {
          if (tg.isNumber(connectOptions.heartbeatInterval)) {
            parsedConnectOptions.heartbeatInterval =
              connectOptions.heartbeatInterval;
          } else {
            errors.push(
              `Invalid type for connectOptions.heartbeatInterval, expected number.`
            );
          }
        }

        if (tg.hasProperty(connectOptions, 'maxDumbHeartbeats')) {
          if (tg.isNumber(connectOptions.maxDumbHeartbeats)) {
            parsedConnectOptions.maxDumbHeartbeats =
              connectOptions.maxDumbHeartbeats;
          } else {
            errors.push(
              `Invalid type for connectOptions.maxDumbHeartbeats, expected number.`
            );
          }
        }

        config.connectOptions = parsedConnectOptions;
      } else {
        errors.push(`Invalid type for connectOptions, expected object.`);
      }
    }
    if (tg.hasProperty(json, 'excludedApps')) {
      if (tg.isStringArray(json.excludedApps)) {
        config.excludedApps = json.excludedApps;
      } else {
        errors.push(`Invalid type for excludedApps, expected string array.`);
      }
    }
    if (tg.hasProperty(json, 'excludedAuthMethods')) {
      if (tg.isStringArray(json.excludedAuthMethods)) {
        config.excludedAuthMethods = json.excludedAuthMethods as AuthMethod[];
      } else {
        errors.push(
          `Invalid type for excludedAuthMethods, expected string array.`
        );
      }
    }
    if (tg.hasProperty(json, 'excludedEDSAgents')) {
      if (tg.isStringArray(json.excludedEDSAgents)) {
        config.excludedEDSAgents = json.excludedEDSAgents as DSAgents[];
      } else {
        errors.push(
          `Invalid type for excludedEDSAgents, expected string array.`
        );
      }
    }
    if (tg.hasProperty(json, 'dsKeyType')) {
      if (tg.isString(json.dsKeyType)) {
        config.dsKeyType = json.dsKeyType as KeyTypes;
      } else {
        errors.push(`Invalid type for dsKeyType, expected string.`);
      }
    }
    if (tg.hasProperty(json, 'allowedDSMediaTypes')) {
      if (tg.isString(json.allowedDSMediaTypes)) {
        config.allowedDSMediaTypes = json.allowedDSMediaTypes;
      } else {
        errors.push(`Invalid type for allowedDSMediaTypes, expected string.`);
      }
    }
    if (tg.hasProperty(json, 'forbiddenDSMediaTypes')) {
      if (tg.isString(json.forbiddenDSMediaTypes)) {
        config.forbiddenDSMediaTypes = json.forbiddenDSMediaTypes;
      } else {
        errors.push(`Invalid type for forbiddenDSMediaTypes, expected string.`);
      }
    }
    if (tg.hasProperty(json, 'pivotLicenseKey')) {
      if (tg.isString(json.pivotLicenseKey)) {
        config.pivotLicenseKey = json.pivotLicenseKey;
      } else {
        errors.push(`Invalid type for pivotLicenseKey, expected string.`);
      }
    }
    if (tg.hasProperty(json, 'stiLicenseKey')) {
      if (tg.isString(json.stiLicenseKey)) {
        config.stiLicenseKey = json.stiLicenseKey;
      } else {
        errors.push(`Invalid type for stiLicenseKey, expected string.`);
      }
    }
    if (tg.hasProperty(json, 'signAlgo')) {
      if (tg.isNumber(json.signAlgo)) {
        config.signAlgo = json.signAlgo as SignAlgo;
      } else {
        errors.push(`Invalid type for signAlgo, expected number.`);
      }
    }
    if (tg.hasProperty(json, 'userGuideUrl')) {
      if (tg.isString(json.userGuideUrl)) {
        config.userGuideUrl = json.userGuideUrl;
      } else {
        errors.push(`Invalid type for userGuideUrl, expected string.`);
      }
    }

    if (errors.length > 0) {
      throw new Error(
        'Invalid API config: \n' + JSON.stringify(errors, null, 2)
      );
    }

    return config;
  }
}

const RuntimeConfigService = (() => {
  let instance: IRuntimeConfigService;
  return new Proxy(RuntimeConfigServiceSingleton, {
    construct(
      Target: typeof RuntimeConfigServiceSingleton
    ): IRuntimeConfigService {
      if (!instance) {
        instance = new Target();
      }
      return instance;
    },
  });
})();

export default RuntimeConfigService;
